import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  useTheme,
  alpha,
  Chip,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  School,
  Assignment,
  Refresh,
  Add,
  AdminPanelSettings,
  PersonAdd,
  Class,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardService } from '../../services/dashboardService';
import { DashboardStats, ActivityLog } from '../../types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: string;
    isPositive: boolean;
    isNeutral?: boolean;
  };
  loading?: boolean;
}

function StatCard({ title, value, icon, color, trend, loading }: StatCardProps) {
  const theme = useTheme();
  
  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: `0 20px 40px -12px ${alpha(color, 0.3)}`,
        },        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha('#ffffff', 0.1)} 0%, transparent 50%)`,
          opacity: 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover::before': {
          opacity: 1,
        },
      }}
    >
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              bgcolor: alpha('#ffffff', 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha('#ffffff', 0.1)}`,
              transition: 'all 0.3s ease',
            }}
          >
                        {React.cloneElement(icon as React.ReactElement<any>, {               sx: { fontSize: 28 }             })}
          </Box>
          {trend && !loading && (
            <Box              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: alpha('#ffffff', 0.15),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha('#ffffff', 0.1)}`,
              }}
            >
              {trend.isNeutral ? (
                <Box sx={{ width: 16, height: 2, bgcolor: 'white', borderRadius: 1 }} />
              ) : trend.isPositive ? (
                <TrendingUp sx={{ fontSize: 16 }} />
              ) : (
                <TrendingDown sx={{ fontSize: 16 }} />
              )}
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {trend.value}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 1,
            fontSize: '2.5rem',
            lineHeight: 1,
            background: loading ? 'none' : `linear-gradient(135deg, white 0%, ${alpha('#ffffff', 0.8)} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
          }}
        >
          {loading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : value}
        </Typography>
        
        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, fontSize: '0.875rem' }}>
          {title}
        </Typography>
        {/* Decorative element */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#ffffff', 0.1)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
      </CardContent>
    </Card>
  );
}

export function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [trends, setTrends] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardStats, trendData, activityData] = await Promise.all([
        DashboardService.getDashboardStats(),
        DashboardService.getAttendanceTrends(),
        DashboardService.getRecentActivity()
      ]);
      setStats(dashboardStats);
      setTrends(trendData);
      setRecentActivity(activityData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const getStatCards = () => {
    if (!stats || !trends) return [];

    return [
      {
        title: 'Total Classes',
        value: stats.totalClasses,
        icon: <School />,
        color: theme.palette.primary.main,
        trend: { 
          value: `+${trends.classesThisWeek} this week`, 
          isPositive: trends.classesThisWeek > 0,
          isNeutral: trends.classesThisWeek === 0
        },
      },
      {
        title: 'Total Students',
        value: stats.totalStudents,
        icon: <People />,
        color: theme.palette.secondary.main,
        trend: { 
          value: `+${trends.studentsThisMonth} this month`, 
          isPositive: trends.studentsThisMonth > 0,
          isNeutral: trends.studentsThisMonth === 0
        },
      },
      {
        title: "Today's Attendance",
        value: `${stats.todayAttendanceRate}%`,
        icon: <Assignment />,
        color: theme.palette.warning.main,
        trend: { 
          value: trends.attendanceComparedToYesterday === 'same' ? 
            'Same as yesterday' : 
            trends.attendanceComparedToYesterday === 'up' ?
              'Higher than yesterday' :
              'Lower than yesterday',
          isPositive: trends.attendanceComparedToYesterday !== 'down',
          isNeutral: trends.attendanceComparedToYesterday === 'same'
        },
      },
      {
        title: 'Weekly Average',
        value: `${stats.weeklyAttendanceRate}%`,
        icon: <TrendingUp />,
        color: theme.palette.success.main,
        trend: { 
          value: `${trends.weeklyAttendanceChange > 0 ? '+' : ''}${trends.weeklyAttendanceChange}% from last week`, 
          isPositive: trends.weeklyAttendanceChange > 0,
          isNeutral: trends.weeklyAttendanceChange === 0
        },
      },
    ];
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours === 1) {
      return '1 hour ago';
    } else if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'success': return { color: theme.palette.success.main };
      case 'warning': return { color: theme.palette.warning.main };
      case 'error': return { color: theme.palette.error.main };
      default: return { color: theme.palette.primary.main };
    }
  };

  return (
    <Box>
      {/* Enhanced Header */}
      <Box
        sx={{
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #434343 0%, #000000 100%)',
          color: 'white',
          p: 4,
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,            backgroundImage: `
              radial-gradient(circle at 20% 80%, ${alpha('#ffffff', 0.1)} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha('#ffffff', 0.1)} 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, ${alpha('#ffffff', 0.05)} 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: 'linear-gradient(135deg, white 0%, rgba(255,255,255,0.8) 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                lineHeight: 1.2,
              }}
            >
              Welcome back, {user?.firstName || (user?.username ? user.username.charAt(0).toUpperCase() + user.username.slice(1) : 'User')}! 👋
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, mb: 3 }}>
              Here's what's happening with your attendance system today.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>              <Chip
                label={user?.role?.toUpperCase()}
                sx={{
                  bgcolor: alpha('#ffffff', 0.15),
                  color: 'white',
                  border: `1px solid ${alpha('#ffffff', 0.2)}`,
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}              sx={{
                borderColor: alpha('#ffffff', 0.3),
                color: 'white',
                backdropFilter: 'blur(10px)',
                bgcolor: alpha('#ffffff', 0.1),
                '&:hover': {
                  borderColor: alpha('#ffffff', 0.5),
                  bgcolor: alpha('#ffffff', 0.2),
                },
              }}
            >
              Refresh
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/admin/users')}                sx={{
                  bgcolor: alpha('#ffffff', 0.15),
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha('#ffffff', 0.2)}`,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.25),
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Quick Action
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {getStatCards().map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Box
              sx={{
                animationDelay: `${index * 0.1}s`,
                animation: loading ? 'none' : 'slideInUp 0.6s ease-out both',
                '@keyframes slideInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(30px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <StatCard {...stat} loading={loading} />
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {/* Main Content Grid */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        {/* Chart Area */}
        <Grid item xs={12} lg={8}>
          <Paper
            sx={{
              p: 4,
              height: 450,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 3,
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                : 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
              position: 'relative',
              overflow: 'hidden',
              animation: loading ? 'none' : 'fadeIn 0.8s ease-out 0.3s both',
              '@keyframes fadeIn': {
                '0%': { opacity: 0 },
                '100%': { opacity: 1 },
              },
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(circle at 25% 25%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 40%),
                  radial-gradient(circle at 75% 75%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 40%)
                `,
                pointerEvents: 'none',
              }}
            />
            
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  color: 'white',
                  boxShadow: `0 10px 30px -10px ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
              >
                <TrendingUp sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Attendance Analytics
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Interactive charts and analytics will be available here
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                View Full Analytics
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* Quick Actions */}
        <Grid item xs={12} lg={4}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              height: 450,
              animation: loading ? 'none' : 'fadeIn 0.8s ease-out 0.5s both',
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,              backgroundImage: `
                radial-gradient(circle at 20% 80%, ${alpha('#ffffff', 0.1)} 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, ${alpha('#ffffff', 0.1)} 0%, transparent 50%)
              `,
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>
                Quick Actions
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {user?.role === 'admin' ? (
                  <>                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<PersonAdd />}
                      onClick={() => navigate('/admin/users')}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 2,
                        borderRadius: 2,
                        borderColor: alpha('#ffffff', 0.3),
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        bgcolor: alpha('#ffffff', 0.1),
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                          borderColor: alpha('#ffffff', 0.6),
                          bgcolor: alpha('#ffffff', 0.2),
                          transform: 'translateX(8px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Manage Users
                    </Button>                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Class />}
                      onClick={() => navigate('/admin/classes')}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 2,
                        borderRadius: 2,
                        borderColor: alpha('#ffffff', 0.3),
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        bgcolor: alpha('#ffffff', 0.1),
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                          borderColor: alpha('#ffffff', 0.6),
                          bgcolor: alpha('#ffffff', 0.2),
                          transform: 'translateX(8px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Manage Classes
                    </Button>                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Assignment />}
                      onClick={() => navigate('/reports')}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 2,
                        borderRadius: 2,
                        borderColor: alpha('#ffffff', 0.3),
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        bgcolor: alpha('#ffffff', 0.1),
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                          borderColor: alpha('#ffffff', 0.6),
                          bgcolor: alpha('#ffffff', 0.2),
                          transform: 'translateX(8px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      View Reports
                    </Button>
                  </>
                ) : (
                  <>                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<School />}
                      onClick={() => navigate('/faculty/dashboard')}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 2,
                        borderRadius: 2,
                        borderColor: alpha('#ffffff', 0.3),
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        bgcolor: alpha('#ffffff', 0.1),
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                          borderColor: alpha('#ffffff', 0.6),
                          bgcolor: alpha('#ffffff', 0.2),
                          transform: 'translateX(8px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      My Classes
                    </Button>                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Assignment />}
                      onClick={() => navigate('/faculty/attendance')}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 2,
                        borderRadius: 2,
                        borderColor: alpha('#ffffff', 0.3),
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        bgcolor: alpha('#ffffff', 0.1),
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                          borderColor: alpha('#ffffff', 0.6),
                          bgcolor: alpha('#ffffff', 0.2),
                          transform: 'translateX(8px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Take Attendance
                    </Button>                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<People />}
                      onClick={() => navigate('/reports')}
                      sx={{
                        justifyContent: 'flex-start',
                        py: 2,
                        borderRadius: 2,
                        borderColor: alpha('#ffffff', 0.3),
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        bgcolor: alpha('#ffffff', 0.1),
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                          borderColor: alpha('#ffffff', 0.6),
                          bgcolor: alpha('#ffffff', 0.2),
                          transform: 'translateX(8px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      View Reports
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              animation: loading ? 'none' : 'fadeIn 0.8s ease-out 0.7s both',
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
                : 'linear-gradient(135deg, #232526 0%, #414345 100%)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(circle at 10% 20%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 40%),
                  radial-gradient(circle at 80% 80%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 40%)
                `,
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  <Assignment sx={{ fontSize: 24 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Recent Activity
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : recentActivity.length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 6 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.text.secondary, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      <Assignment sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No Recent Activity
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Activity will appear here as users interact with the system
                    </Typography>
                  </Box>
                ) : (
                  recentActivity.map((activity, index) => (
                    <Box
                      key={activity.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        p: 3,                        bgcolor: theme.palette.mode === 'light'
                          ? alpha('#ffffff', 0.7)
                          : alpha(theme.palette.background.paper, 0.7),
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[4],
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: getActivityIcon(activity.type).color,
                          boxShadow: `0 0 0 4px ${alpha(getActivityIcon(activity.type).color, 0.2)}`,
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {activity.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeAgo(new Date(activity.timestamp))}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}